// Staff Board Page - Beautiful Cards with Rank Badges
import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Users, Plus, Search, User, Phone, Mail, 
  MoreVertical, Edit, Key, Copy, Check, ChevronRight, Tag, X, Trash2, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { usersApi, wacheApi, tagsApi, permissionsApi, dienstApi, kategorienApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { canManageRole, canChangeWachabteilung } from '../lib/rolePermissions';
import { FahrzeugFreigabenEditor } from '../components/mitarbeiter/FahrzeugFreigabenEditor';
import { cn, roleLabels, qualifikationLabels } from '../lib/utils';
import { compareByDienstgrad } from '../lib/dienstgrade';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Vollständiges Dienstgrad-Mapping mit allen Abzeichen
const dienstgradConfig = {
  // Mittlerer Dienst (rot)
  'BM-A1': { image: '/images/dienstgrade/bm-a1.svg', label: 'Brandmeisteranwärter (1. Jahr)', category: 'md' },
  'BM-A2': { image: '/images/dienstgrade/bm-a2.svg', label: 'Brandmeisteranwärter (2. Jahr)', category: 'md' },
  'BM-A': { image: '/images/dienstgrade/bm-a2.svg', label: 'Brandmeisteranwärter', category: 'md' },
  'BM': { image: '/images/dienstgrade/bm.svg', label: 'Brandmeister', category: 'md' },
  'OBM': { image: '/images/dienstgrade/obm.svg', label: 'Oberbrandmeister', category: 'md' },
  'HBM': { image: '/images/dienstgrade/hbm.svg', label: 'Hauptbrandmeister', category: 'md' },
  'HBMZ': { image: '/images/dienstgrade/hbmz.svg', label: 'Hauptbrandmeister m.Z.', category: 'md' },
  
  // Gehobener Dienst (silber)
  'BOI-A1': { image: '/images/dienstgrade/boi-a1.svg', label: 'Brandoberinspektoranwärter (1. Jahr)', category: 'gd' },
  'BOI-A2': { image: '/images/dienstgrade/boi-a2.svg', label: 'Brandoberinspektoranwärter (2. Jahr)', category: 'gd' },
  'BI': { image: '/images/dienstgrade/bi.svg', label: 'Brandinspektor', category: 'gd' },
  'BOI': { image: '/images/dienstgrade/boi.svg', label: 'Brandoberinspektor', category: 'gd' },
  'BA': { image: '/images/dienstgrade/ba.svg', label: 'Brandamtmann', category: 'gd' },
  'BAR': { image: '/images/dienstgrade/bar.svg', label: 'Brandamtsrat', category: 'gd' },
  'BOAR': { image: '/images/dienstgrade/boar.svg', label: 'Brandoberamtsrat', category: 'gd' },
  'BOARZ': { image: '/images/dienstgrade/boarz.svg', label: 'Brandoberamtsrat m.Z.', category: 'gd' },
  
  // Höherer Dienst (gold)
  'BREF': { image: '/images/dienstgrade/bref.svg', label: 'Brandreferendar', category: 'hd' },
  'BR': { image: '/images/dienstgrade/br.svg', label: 'Brandrat', category: 'hd' },
  'BOR': { image: '/images/dienstgrade/bor.svg', label: 'Brandoberrat', category: 'hd' },
  'BD': { image: '/images/dienstgrade/bd.svg', label: 'Branddirektor', category: 'hd' },
  'LTDBD': { image: '/images/dienstgrade/ltdbd.svg', label: 'Leitender Branddirektor', category: 'hd' },
  'LBDV': { image: '/images/dienstgrade/lbdv.svg', label: 'Vertreter Landesbranddirektor', category: 'hd' },
  'LBD': { image: '/images/dienstgrade/lbd.svg', label: 'Landesbranddirektor', category: 'hd' },
  
  // Ärztlicher Dienst
  'MEDR': { image: '/images/dienstgrade/medr.svg', label: 'Medizinalrat', category: 'ad' },
  'MEDOR': { image: '/images/dienstgrade/medor.svg', label: 'Medizinaloberrat', category: 'ad' },
  'MEDDIR': { image: '/images/dienstgrade/meddir.svg', label: 'Medizinaldirektor', category: 'ad' },
  'LTDMEDDIR': { image: '/images/dienstgrade/ltdmeddir.svg', label: 'Leitender Medizinaldirektor', category: 'ad' },
};

const dienstgradOptions = [
  { group: 'Mittlerer Dienst', options: [
    { value: 'BM-A1', label: 'Brandmeisteranwärter (1. Jahr)' },
    { value: 'BM-A2', label: 'Brandmeisteranwärter (2. Jahr)' },
    { value: 'BM', label: 'Brandmeister' },
    { value: 'OBM', label: 'Oberbrandmeister' },
    { value: 'HBM', label: 'Hauptbrandmeister' },
    { value: 'HBMZ', label: 'Hauptbrandmeister m.Z.' },
  ]},
  { group: 'Gehobener Dienst', options: [
    { value: 'BI', label: 'Brandinspektor' },
    { value: 'BOI', label: 'Brandoberinspektor' },
    { value: 'BA', label: 'Brandamtmann' },
    { value: 'BAR', label: 'Brandamtsrat' },
    { value: 'BOAR', label: 'Brandoberamtsrat' },
  ]},
  { group: 'Höherer Dienst', options: [
    { value: 'BR', label: 'Brandrat' },
    { value: 'BOR', label: 'Brandoberrat' },
    { value: 'BD', label: 'Branddirektor' },
    { value: 'LTDBD', label: 'Leitender Branddirektor' },
  ]},
];

const qualifikationOptions = [
  { value: 'RS', label: 'Rettungssanitäter' },
  { value: 'RA', label: 'Rettungsassistent' },
  { value: 'NotSan', label: 'Notfallsanitäter' },
  { value: 'RH', label: 'Rettungshelfer' },
];

const rolleOptions = [
  { value: 'MITARBEITER', label: 'Mitarbeiter' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'WACHABTEILUNGSLEITUNG', label: 'Wachabteilungsleitung' },
  { value: 'WACHLEITUNG_VERTRETUNG', label: 'Wachleitung (Vertretung)' },
  { value: 'WACHLEITUNG', label: 'Wachleitung' },
];

// Schöne Dienstgrad-Badge Komponente
function DienstgradBadge({ dienstgrad, size = 'md', showLabel = false }) {
  const config = dienstgradConfig[dienstgrad?.toUpperCase()];
  
  const sizeClasses = {
    sm: 'w-8 h-10',
    md: 'w-10 h-12',
    lg: 'w-14 h-16',
  };

  if (!config) {
    return (
      <div className={cn(
        'flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800',
        sizeClasses[size]
      )}>
        <span className="text-[10px] font-bold text-gray-400">{dienstgrad || '—'}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <div className={cn(
        'flex items-center justify-center p-1',
        sizeClasses[size]
      )}>
        <img 
          src={config.image} 
          alt={config.label}
          className="w-full h-full object-contain drop-shadow-sm"
          title={config.label}
        />
      </div>
      {showLabel && (
        <span className="text-[10px] font-medium text-muted-foreground text-center leading-tight">
          {dienstgrad}
        </span>
      )}
    </div>
  );
}

// Mitarbeiter Detail/Bearbeiten Dialog
function UserDetailDialog({ user, open, onOpenChange, abteilungen, onUpdate, canEdit, availableTags, availableKategorien = [], canDelete, canSetZusatzberechtigungen, availablePermissions, currentUserRolle, fahrzeuge = [] }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [newPasswordModal, setNewPasswordModal] = useState({ open: false, password: '' });
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        vorname: user.vorname || '',
        nachname: user.nachname || '',
        loginname: user.loginname || '',
        email: user.email || '',
        telefon: user.telefon || '',
        dienstgrad: user.dienstgrad || '',
        qualifikation: user.qualifikation || '',
        abteilung_id: user.abteilung_id || '',
        rolle: user.rolle || 'MITARBEITER',
        ist_aktiv: user.ist_aktiv ?? true,
        ist_gast: user.ist_gast ?? false,
        tag_ids: user.tag_ids || [],
        kategorien_ids: user.kategorien_ids || [],
        zusatzberechtigungen: user.zusatzberechtigungen || [],
        gesperrte_funktionen: user.gesperrte_funktionen || [],
      });
      setIsEditing(false);
    }
  }, [user, open]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await usersApi.update(user.id, {
        ...formData,
        email: formData.email || null,
        telefon: formData.telefon || null,
        dienstgrad: formData.dienstgrad || null,
        qualifikation: formData.qualifikation || null,
        tag_ids: formData.tag_ids || [],
        kategorien_ids: formData.kategorien_ids || [],
        zusatzberechtigungen: formData.zusatzberechtigungen || [],
        gesperrte_funktionen: formData.gesperrte_funktionen || [],
      });
      toast.success('Mitarbeiter aktualisiert');
      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Update error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Speichern';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTag = (tagId) => {
    setFormData(prev => {
      const current = prev.tag_ids || [];
      if (current.includes(tagId)) {
        return { ...prev, tag_ids: current.filter(id => id !== tagId) };
      } else {
        return { ...prev, tag_ids: [...current, tagId] };
      }
    });
  };

  // Get tag names from IDs
  const userTags = (
    user?.effective_tag_ids?.length ? user.effective_tag_ids : (user?.tag_ids || [])
  )
    .map(id => availableTags.find(t => t.id === id))
    .filter(Boolean);

  const handleResetPassword = async () => {
    setIsResetting(true);
    try {
      const res = await usersApi.resetPassword(user.id);
      const newPwd = res.data.temp_passwort || res.data.temporaeres_passwort;
      setResetConfirmOpen(false);
      setPasswordCopied(false);
      setNewPasswordModal({ open: true, password: newPwd });
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Passwort zurücksetzen fehlgeschlagen');
    } finally {
      setIsResetting(false);
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPasswordModal.password);
      setPasswordCopied(true);
      toast.success('Passwort in Zwischenablage kopiert');
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await usersApi.deactivate(user.id);
      toast.success(`${user.vorname} ${user.nachname} wurde deaktiviert`);
      setDeleteConfirmOpen(false);
      onOpenChange(false);
      onUpdate?.();
    } catch (error) {
      console.error('Delete error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Löschen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) return null;

  const dienstgradInfo = dienstgradConfig[user.dienstgrad?.toUpperCase()];

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 ring-2 ring-primary/20">
              <AvatarImage src={user.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-xl font-bold">
                {user.vorname?.[0]}{user.nachname?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <DialogTitle className="text-xl">
                {isEditing ? 'Mitarbeiter bearbeiten' : `${user.vorname} ${user.nachname}`}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{roleLabels[user.rolle]}</Badge>
                {dienstgradInfo && <span className="text-xs">{dienstgradInfo.label}</span>}
              </DialogDescription>
            </div>
            {user.dienstgrad && (
              <DienstgradBadge dienstgrad={user.dienstgrad} size="lg" />
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {isEditing ? (
            // Bearbeitungs-Formular
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vorname</Label>
                  <Input
                    value={formData.vorname}
                    onChange={(e) => setFormData(p => ({ ...p, vorname: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nachname</Label>
                  <Input
                    value={formData.nachname}
                    onChange={(e) => setFormData(p => ({ ...p, nachname: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Loginname</Label>
                <Input
                  value={formData.loginname}
                  onChange={(e) => setFormData(p => ({ ...p, loginname: e.target.value }))}
                  className="font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>E-Mail</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    value={formData.telefon}
                    onChange={(e) => setFormData(p => ({ ...p, telefon: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dienstgrad</Label>
                  <Select
                    value={formData.dienstgrad || 'none'}
                    onValueChange={(v) => setFormData(p => ({ ...p, dienstgrad: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keiner —</SelectItem>
                      {dienstgradOptions.map(group => (
                        <React.Fragment key={group.group}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                            {group.group}
                          </div>
                          {group.options.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Qualifikation</Label>
                  <Select
                    value={formData.qualifikation || 'none'}
                    onValueChange={(v) => setFormData(p => ({ ...p, qualifikation: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keine —</SelectItem>
                      {qualifikationOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Abteilung nur für nicht-Leitungsrollen. Änderung nur ab WL-V aufwärts. */}
                {formData.rolle !== 'WACHLEITUNG' && formData.rolle !== 'WACHLEITUNG_VERTRETUNG' && (() => {
                  const abtEditable = canChangeWachabteilung(currentUserRolle);
                  return (
                    <div className="space-y-2">
                      <Label>
                        Abteilung
                        {!abtEditable && (
                          <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                            (nur Wachleitung ändert diese)
                          </span>
                        )}
                      </Label>
                      <Select
                        value={formData.abteilung_id || ''}
                        onValueChange={(v) => setFormData(p => ({ ...p, abteilung_id: v }))}
                        disabled={!abtEditable}
                      >
                        <SelectTrigger data-testid="edit-abteilung-select">
                          <SelectValue placeholder="Auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {abteilungen.map(abt => (
                            <SelectItem key={abt.id} value={abt.id}>{abt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
                <div className="space-y-2">
                  <Label>Hauptrolle</Label>
                  <Select
                    value={formData.rolle}
                    onValueChange={(v) => setFormData(p => ({ ...p, rolle: v }))}
                  >
                    <SelectTrigger data-testid="edit-rolle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {rolleOptions
                        .filter(opt => canManageRole(currentUserRolle, opt.value) || opt.value === formData.rolle)
                        .map(opt => {
                          const disabled = !canManageRole(currentUserRolle, opt.value);
                          return (
                            <SelectItem
                              key={opt.value}
                              value={opt.value}
                              disabled={disabled}
                            >
                              {opt.label}
                              {disabled && (
                                <span className="ml-2 text-xs text-muted-foreground">(aktuelle Rolle)</span>
                              )}
                            </SelectItem>
                          );
                        })
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Gast-Toggle: außerhalb der Stammkraft, separater Block in der Einteilung */}
              <div className="space-y-2 p-3 rounded-lg border-2 border-dashed border-violet-300 bg-violet-50/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ist_gast || false}
                    onChange={(e) => setFormData(p => ({ ...p, ist_gast: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="edit-ist-gast-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-violet-900">Gast-Mitarbeiter</p>
                    <p className="text-xs text-violet-700/80">
                      Wird im Gäste-Block der Einteilung angezeigt (nicht zur regulären Stammkraft zählend).
                      Geeignet für externe Aushilfen / Wachfremde, die unregelmäßig Dienste machen.
                    </p>
                  </div>
                </label>
              </div>

              {/* Zusatzberechtigungen — nur sichtbar für WL/WLV/WAL */}
              {canSetZusatzberechtigungen && (
                <div className="space-y-2 p-3 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50/30">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-700" />
                    <Label className="text-sm font-semibold text-emerald-900">Persönliche Ausnahmen (Übergangsphase)</Label>
                  </div>
                  <p className="text-xs text-emerald-700/80">
                    Granulare Rechte zusätzlich zur Rolle. Z.B. einem Mitarbeiter
                    erlauben, die Einteilung der eigenen Wachabteilung zu bearbeiten.
                  </p>
                  <div className="space-y-1 mt-2">
                    {availablePermissions.map(perm => {
                      const isChecked = (formData.zusatzberechtigungen || []).includes(perm.key);
                      return (
                        <label key={perm.key} className="flex items-center gap-2 cursor-pointer hover:bg-emerald-100/40 rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              setFormData(p => {
                                const cur = p.zusatzberechtigungen || [];
                                return {
                                  ...p,
                                  zusatzberechtigungen: e.target.checked
                                    ? [...cur, perm.key]
                                    : cur.filter(k => k !== perm.key),
                                };
                              });
                            }}
                            data-testid={`zusatzrecht-${perm.key}`}
                          />
                          <span className="text-sm text-emerald-900">{perm.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tags-Auswahl */}
              {availableTags.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Tags und Qualifikationen
                  </Label>
                  <div className="flex flex-wrap gap-2 p-3 rounded-lg border bg-muted/20">
                    {availableTags.map(tag => {
                      const isSelected = formData.tag_ids?.includes(tag.id);
                      const isNegativ = tag.kategorie === 'LIZENZ_NEGATIV';
                      const farbe = tag.farbe || '#64748b';
                      return (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          className={cn(
                            'cursor-pointer transition-all border-2 font-semibold',
                            isSelected ? 'text-white shadow-sm' : 'bg-white hover:bg-muted/40',
                            isNegativ && isSelected && 'line-through decoration-2 decoration-white/70'
                          )}
                          style={isSelected
                            ? { backgroundColor: farbe, borderColor: farbe }
                            : { borderColor: farbe, color: farbe }}
                          onClick={() => toggleTag(tag.id)}
                          data-testid={`tag-toggle-${tag.id}`}
                        >
                          {tag.name}
                          {isSelected && <X className="w-3 h-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Benutzergruppen-Auswahl (Mehrfachauswahl) */}
              {availableKategorien.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Zusätzliche Benutzergruppen
                  </Label>
                  <div className="flex flex-wrap gap-2 p-3 rounded-lg border bg-muted/20">
                    {availableKategorien.map(k => {
                      const isSelected = formData.kategorien_ids?.includes(k.id);
                      const farbe = k.farbe || '#64748b';
                      return (
                        <Badge
                          key={k.id}
                          variant="outline"
                          className={cn(
                            'cursor-pointer transition-all border-2 font-semibold',
                            isSelected ? 'text-white shadow-sm' : 'bg-white hover:bg-muted/40'
                          )}
                          style={isSelected
                            ? { backgroundColor: farbe, borderColor: farbe }
                            : { borderColor: farbe, color: farbe }}
                          onClick={() => setFormData(prev => {
                            const cur = prev.kategorien_ids || [];
                            return cur.includes(k.id)
                              ? { ...prev, kategorien_ids: cur.filter(id => id !== k.id) }
                              : { ...prev, kategorien_ids: [...cur, k.id] };
                          })}
                          data-testid={`kategorie-toggle-${k.id}`}
                        >
                          {k.name}
                          {isSelected && <X className="w-3 h-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fahrzeug-Freigaben (Blacklist) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Fahrzeug-Freigaben
                </Label>
                <div className="rounded-lg border bg-muted/10 p-3">
                  <FahrzeugFreigabenEditor
                    fahrzeuge={fahrzeuge}
                    gesperrteFunktionen={formData.gesperrte_funktionen || []}
                    onChange={(neu) => setFormData(p => ({ ...p, gesperrte_funktionen: neu }))}
                  />
                </div>
              </div>
            </>
          ) : (
            // Ansichts-Modus
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-muted/30">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Loginname</p>
                  <p className="font-mono font-semibold">{user.loginname}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Abteilung</p>
                  <p className="font-semibold">{abteilungen.find(a => a.id === user.abteilung_id)?.name || '—'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">E-Mail</p>
                    <p className="font-medium text-sm">{user.email || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Telefon</p>
                    <p className="font-medium text-sm">{user.telefon || '—'}</p>
                  </div>
                </div>
              </div>

              {(user.dienstgrad || user.qualifikation) && (
                <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-transparent border">
                  {user.dienstgrad && (
                    <div className="flex items-center gap-3">
                      <DienstgradBadge dienstgrad={user.dienstgrad} size="md" />
                      <div>
                        <p className="text-xs text-muted-foreground">Dienstgrad</p>
                        <p className="font-semibold">{dienstgradInfo?.label || user.dienstgrad}</p>
                      </div>
                    </div>
                  )}
                  {user.qualifikation && (
                    <div className="border-l pl-4">
                      <p className="text-xs text-muted-foreground">Qualifikation</p>
                      <p className="font-semibold">{qualifikationLabels[user.qualifikation] || user.qualifikation}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Tags / Lizenz-Pills — alle Tags des Mitarbeiters als kräftige Pills */}
              {userTags.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Tags / Lizenzen
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {userTags.map((tag) => {
                      const isNegativ = tag.kategorie === 'LIZENZ_NEGATIV';
                      const farbe = tag.farbe || '#64748b';
                      const displayName = isNegativ
                        ? tag.name.replace(/^Kein(e|en|er|es)?\s+/i, '')
                        : tag.name;
                      return (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          className={cn(
                            'text-xs px-2 py-0.5 font-semibold text-white border-2',
                            isNegativ && 'line-through decoration-white/80'
                          )}
                          style={{ backgroundColor: farbe, borderColor: farbe }}
                          title={tag.name}
                        >
                          {displayName}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)} className="sm:mr-auto">
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Speichere...' : 'Speichern'}
              </Button>
            </>
          ) : canEdit ? (
            <>
              <div className="flex gap-2 sm:mr-auto">
                <Button variant="outline" onClick={() => setResetConfirmOpen(true)} data-testid="reset-pw-btn">
                  <Key className="w-4 h-4 mr-2" />
                  Passwort
                </Button>
                {canDelete && (
                  <Button 
                    variant="outline" 
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Löschen
                  </Button>
                )}
              </div>
              <Button onClick={() => setIsEditing(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Bearbeiten
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Mitarbeiter löschen?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Sind Sie sicher, dass Sie <span className="font-semibold">{user?.vorname} {user?.nachname}</span> deaktivieren möchten?
            <br />
            <span className="text-muted-foreground text-sm mt-2 block">
              Der Mitarbeiter wird deaktiviert und erscheint nicht mehr in der Übersicht. 
              Diese Aktion kann rückgängig gemacht werden.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Wird gelöscht...' : 'Deaktivieren'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Reset Password Confirmation */}
    <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-600" />
            Passwort zurücksetzen?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Soll für <span className="font-semibold">{user?.vorname} {user?.nachname}</span> wirklich ein neues temporäres Passwort vergeben werden?
            <br />
            <span className="text-muted-foreground text-sm mt-2 block">
              Das bisherige Passwort wird sofort ungültig. Der Mitarbeiter muss das neue Passwort beim nächsten Login ändern.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isResetting}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleResetPassword}
            disabled={isResetting}
            data-testid="reset-pw-confirm-btn"
          >
            {isResetting ? 'Wird zurückgesetzt...' : 'Passwort vergeben'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* New Password Display Modal */}
    <Dialog
      open={newPasswordModal.open}
      onOpenChange={(open) => {
        if (!open) {
          setNewPasswordModal({ open: false, password: '' });
          setPasswordCopied(false);
        }
      }}
    >
      <DialogContent data-testid="new-pw-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-emerald-600" />
            Neues temporäres Passwort
          </DialogTitle>
          <DialogDescription>
            Für: <span className="font-semibold">{user?.vorname} {user?.nachname}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleCopyPassword}
            className="group w-full p-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:border-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all cursor-pointer"
            data-testid="new-pw-copy-btn"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-left">
                <p className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 font-medium">
                  Klicken zum Kopieren
                </p>
                <p className="font-mono font-bold text-2xl text-amber-900 dark:text-amber-100 mt-1 break-all">
                  {newPasswordModal.password}
                </p>
              </div>
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                {passwordCopied ? (
                  <Check className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Copy className="w-5 h-5 text-amber-700 dark:text-amber-200" />
                )}
              </div>
            </div>
          </button>
          <p className="text-xs text-muted-foreground">
            Geben Sie das Passwort dem Mitarbeiter weiter. Es muss beim nächsten Login geändert werden.
          </p>
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              setNewPasswordModal({ open: false, password: '' });
              setPasswordCopied(false);
            }}
            data-testid="new-pw-close-btn"
          >
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// Mitarbeiter hinzufügen Dialog
function AddUserDialog({ open, onOpenChange, abteilungen, onSuccess, currentUserRolle, currentUserAbteilungId, availableKategorien = [], availableTags = [] }) {
  const [isLoading, setIsLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const abtEditable = canChangeWachabteilung(currentUserRolle);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    loginname: '',
    email: '',
    telefon: '',
    dienstgrad: '',
    qualifikation: '',
    abteilung_id: '',
    rolle: 'MITARBEITER',
    kategorien_ids: [],
    tag_ids: [],
  });

  useEffect(() => {
    // Bei ADMIN/WAL wird die Abteilung vom Backend auf die eigene gesetzt
    // → im UI direkt die eigene Abteilung anzeigen und disabled.
    if (!abtEditable && currentUserAbteilungId && !formData.abteilung_id) {
      setFormData(prev => ({ ...prev, abteilung_id: currentUserAbteilungId }));
    } else if (abteilungen.length > 0 && !formData.abteilung_id) {
      setFormData(prev => ({ ...prev, abteilung_id: abteilungen[0].id }));
    }
  }, [abteilungen, abtEditable, currentUserAbteilungId]);

  useEffect(() => {
    if (!open) {
      setCreatedUser(null);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Abteilung ist nur für reguläre Rollen erforderlich
    const isLeitungsRolle = formData.rolle === 'WACHLEITUNG' || formData.rolle === 'WACHLEITUNG_VERTRETUNG';
    
    if (!formData.vorname || !formData.nachname || !formData.loginname) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    if (!isLeitungsRolle && !formData.abteilung_id) {
      toast.error('Bitte wählen Sie eine Abteilung');
      return;
    }
    
    setIsLoading(true);
    try {
      const cleanedData = {
        vorname: formData.vorname,
        nachname: formData.nachname,
        loginname: formData.loginname,
        email: formData.email || null,
        telefon: formData.telefon || null,
        dienstgrad: formData.dienstgrad || null,
        qualifikation: formData.qualifikation || null,
        abteilung_id: isLeitungsRolle ? null : formData.abteilung_id,
        rolle: formData.rolle,
        kategorien_ids: formData.kategorien_ids || [],
        tag_ids: formData.tag_ids || [],
        ist_aktiv: true,
        ist_gast: !!formData.ist_gast,
      };
      
      const response = await usersApi.create(cleanedData);
      setCreatedUser(response.data);
      onSuccess?.();
    } catch (error) {
      console.error('Create user error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Erstellen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setFormData({
      vorname: '',
      nachname: '',
      loginname: '',
      email: '',
      telefon: '',
      dienstgrad: '',
      qualifikation: '',
      abteilung_id: abteilungen[0]?.id || '',
      rolle: 'MITARBEITER',
      kategorien_ids: [],
      tag_ids: [],
    });
    setCreatedUser(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  if (createdUser) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <Check className="w-6 h-6" />
              Mitarbeiter erstellt!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-muted/30 rounded-xl">
              <p className="text-lg font-semibold">
                {createdUser.vorname} {createdUser.nachname}
              </p>
              <p className="text-muted-foreground">{roleLabels[createdUser.rolle]}</p>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-3">
                Zugangsdaten (bitte notieren!)
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 p-3 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Loginname</p>
                    <p className="font-mono font-bold">{createdUser.loginname}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard(createdUser.loginname)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 p-3 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Temporäres Passwort</p>
                    <p className="font-mono font-bold text-amber-600">{createdUser.temporaeres_passwort}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard(createdUser.temporaeres_passwort)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                Der Mitarbeiter muss beim ersten Login das Passwort ändern.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose} className="w-full">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Mitarbeiter hinzufügen</DialogTitle>
          <DialogDescription>
            Neuen Mitarbeiter zur Wache hinzufügen
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vorname *</Label>
              <Input
                value={formData.vorname}
                onChange={(e) => setFormData(p => ({ ...p, vorname: e.target.value }))}
                placeholder="Max"
              />
            </div>
            <div className="space-y-2">
              <Label>Nachname *</Label>
              <Input
                value={formData.nachname}
                onChange={(e) => setFormData(p => ({ ...p, nachname: e.target.value }))}
                placeholder="Mustermann"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Loginname *</Label>
            <Input
              value={formData.loginname}
              onChange={(e) => setFormData(p => ({ ...p, loginname: e.target.value }))}
              placeholder="mmustermann"
              className="font-mono"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dienstgrad</Label>
              <Select
                value={formData.dienstgrad || 'none'}
                onValueChange={(v) => setFormData(p => ({ ...p, dienstgrad: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Keiner —</SelectItem>
                  {dienstgradOptions.map(group => (
                    <React.Fragment key={group.group}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                        {group.group}
                      </div>
                      {group.options.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Qualifikation</Label>
              <Select
                value={formData.qualifikation || 'none'}
                onValueChange={(v) => setFormData(p => ({ ...p, qualifikation: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Keine —</SelectItem>
                  {qualifikationOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hauptrolle</Label>
              <Select
                value={formData.rolle}
                onValueChange={(v) => setFormData(p => ({ ...p, rolle: v }))}
              >
                <SelectTrigger data-testid="create-rolle-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rolleOptions
                    .filter(opt => canManageRole(currentUserRolle, opt.value))
                    .map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            {/* Abteilung nur für nicht-Leitungsrollen. Wechsel nur ab WL-V. */}
            {formData.rolle !== 'WACHLEITUNG' && formData.rolle !== 'WACHLEITUNG_VERTRETUNG' && (
              <div className="space-y-2">
                <Label>
                  Abteilung *
                  {!abtEditable && (
                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                      (eigene Wachabteilung)
                    </span>
                  )}
                </Label>
                <Select
                  value={formData.abteilung_id}
                  onValueChange={(v) => setFormData(p => ({ ...p, abteilung_id: v }))}
                  disabled={!abtEditable}
                >
                  <SelectTrigger data-testid="create-abteilung-select">
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {abteilungen.map(abt => (
                      <SelectItem key={abt.id} value={abt.id}>{abt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {availableKategorien.length > 0 && (
            <div className="space-y-2">
              <Label>Zusätzliche Benutzergruppen</Label>
              <div className="flex flex-wrap gap-2 rounded-lg border p-3">
                {availableKategorien.map((group) => {
                  const selected = (formData.kategorien_ids || []).includes(group.id);
                  return (
                    <Badge
                      key={group.id}
                      variant="outline"
                      className="cursor-pointer border-2"
                      style={selected
                        ? { backgroundColor: group.farbe, borderColor: group.farbe, color: 'white' }
                        : { borderColor: group.farbe, color: group.farbe }}
                      onClick={() => setFormData((current) => ({
                        ...current,
                        kategorien_ids: selected
                          ? current.kategorien_ids.filter((id) => id !== group.id)
                          : [...current.kategorien_ids, group.id],
                      }))}
                    >
                      {group.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {availableTags.length > 0 && (
            <div className="space-y-2">
              <Label>Tags und Qualifikationen</Label>
              <div className="flex flex-wrap gap-2 rounded-lg border p-3">
                {availableTags.map((tag) => {
                  const selected = (formData.tag_ids || []).includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className="cursor-pointer border-2"
                      style={selected
                        ? { backgroundColor: tag.farbe, borderColor: tag.farbe, color: 'white' }
                        : { borderColor: tag.farbe, color: tag.farbe }}
                      onClick={() => setFormData((current) => ({
                        ...current,
                        tag_ids: selected
                          ? current.tag_ids.filter((id) => id !== tag.id)
                          : [...current.tag_ids, tag.id],
                      }))}
                    >
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gast-Toggle */}
          <div className="space-y-2 p-3 rounded-lg border-2 border-dashed border-violet-300 bg-violet-50/30">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.ist_gast || false}
                onChange={(e) => setFormData(p => ({ ...p, ist_gast: e.target.checked }))}
                className="mt-0.5"
                data-testid="create-ist-gast-toggle"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-violet-900">Gast-Mitarbeiter</p>
                <p className="text-xs text-violet-700/80">
                  Wird im Gäste-Block der Einteilung angezeigt (nicht zur regulären Stammkraft zählend).
                </p>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Wird erstellt...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Schöne Mitarbeiter-Karte
function StaffCard({ user, onClick, isDragging, availableTags = [] }) {
  const dienstgradInfo = dienstgradConfig[user.dienstgrad?.toUpperCase()];
  const isNotSan = user.qualifikation === 'NotSan';
  
  // Get tag names from IDs
  const userTags = (
    user.effective_tag_ids?.length ? user.effective_tag_ids : (user.tag_ids || [])
  )
    .map(id => availableTags.find(t => t.id === id))
    .filter(Boolean);
  
  return (
    <div 
      onClick={onClick}
      className={cn(
        'group relative bg-white dark:bg-gray-900 rounded-xl cursor-pointer transition-all duration-200 overflow-hidden',
        'border-2 border-transparent hover:border-primary/30',
        'shadow-sm hover:shadow-lg hover:-translate-y-0.5',
        isDragging && 'shadow-xl ring-2 ring-primary scale-[1.02]'
      )}
    >
      {/* NotSan subtle watermark - large, barely visible in background */}
      {isNotSan && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="text-[60px] font-black text-blue-500/[0.06] dark:text-blue-400/[0.08] select-none tracking-wider rotate-[-8deg]">
            NotSan
          </span>
        </div>
      )}
      
      <div className="relative flex items-center p-3 gap-3">
        {/* Dienstgrad Badge - Links */}
        <div className="shrink-0 w-12 flex flex-col items-center justify-center">
          {user.dienstgrad ? (
            <DienstgradBadge dienstgrad={user.dienstgrad} size="md" showLabel />
          ) : (
            <div className="w-10 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm truncate">
              {user.vorname} {user.nachname}
            </h4>
            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </div>
          
          {/* Qualifikation & Tags */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {user.qualifikation && (
              <Badge 
                variant="secondary" 
                className={cn(
                  'text-[10px] px-1.5 py-0',
                  user.qualifikation === 'NotSan' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                  user.qualifikation === 'RS' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                  user.qualifikation === 'RA' && 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
                )}
              >
                {user.qualifikation}
              </Badge>
            )}
            {/* Custom tags from database - kräftige Farben + Durchstreichen für negative Lizenz-Tags */}
            {userTags.slice(0, 3).map((tag) => {
              const isNegativ = tag.kategorie === 'LIZENZ_NEGATIV';
              const farbe = tag.farbe || '#64748b';
              // Negative Tags: "Kein " Präfix entfernen, damit nicht "Kein DLK" durchgestrichen
              // angezeigt wird (= doppelte Negation). Stattdessen z.B. nur "DLK" mit line-through.
              const displayName = isNegativ
                ? tag.name.replace(/^Kein(e|en|er|es)?\s+/i, '')
                : tag.name;
              return (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className={cn(
                    'text-[10px] px-1.5 py-0 font-semibold text-white border-2',
                    isNegativ && 'line-through decoration-white/80'
                  )}
                  style={{ backgroundColor: farbe, borderColor: farbe }}
                  title={tag.name}
                >
                  {displayName}
                </Badge>
              );
            })}
            {userTags.length > 3 && (
              <span className="text-[10px] text-muted-foreground">
                +{userTags.length - 3}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Abteilungs-Spalte
function DepartmentColumn({ abteilung, users, onUserClick, canDragDrop, availableTags }) {
  const columnId = abteilung.id;

  return (
    <div className="flex flex-col min-w-[260px] max-w-[320px] flex-1">
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-2 mb-2">
        <div>
          <h3 className="font-bold text-sm">{abteilung.name}</h3>
          <p className="text-xs text-muted-foreground">{users.length} Mitarbeiter</p>
        </div>
      </div>

      {/* Cards Container - no individual scroll */}
      <Droppable droppableId={columnId} isDropDisabled={!canDragDrop}>
        {(provided, snapshot) => (
          <div 
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              'flex-1 p-2 space-y-2 rounded-xl min-h-[200px]',
              'bg-gradient-to-b from-muted/30 to-muted/10',
              snapshot.isDraggingOver && 'bg-primary/5 ring-2 ring-primary/20 ring-inset'
            )}
          >
            {users.map((user, index) => (
              <Draggable 
                key={user.id} 
                draggableId={user.id} 
                index={index}
                isDragDisabled={!canDragDrop}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <StaffCard 
                      user={user} 
                      onClick={() => onUserClick(user)}
                      isDragging={snapshot.isDragging}
                      availableTags={availableTags}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            
            {users.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Keine Mitarbeiter</p>
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

export function MitarbeiterPage() {
  const { user: currentUser, canSetZusatzberechtigungen: canSetZB } = useAuthStore();
  const [abteilungen, setAbteilungen] = useState([]);
  const [usersByAbteilung, setUsersByAbteilung] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [availableKategorien, setAvailableKategorien] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const canEdit = ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'].includes(currentUser?.rolle);
  const canDelete = ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG'].includes(currentUser?.rolle);
  const canDragDrop = ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG'].includes(currentUser?.rolle);
  const canSetZusatzberechtigungen = !!canSetZB?.();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [abtRes, usersRes, tagsRes, permRes, fzRes, katRes] = await Promise.all([
        wacheApi.getAbteilungen(),
        usersApi.getAll({ ist_aktiv: true }),
        tagsApi.getAll().catch(() => ({ data: [] })),
        permissionsApi.getAvailable().catch(() => ({ data: { permissions: [] } })),
        dienstApi.getFahrzeuge().catch(() => ({ data: [] })),
        kategorienApi.list().catch(() => ({ data: [] })),
      ]);

      setAbteilungen(abtRes.data);
      setAvailableTags(tagsRes.data || []);
      setAvailableKategorien(katRes.data || []);
      setAvailablePermissions(permRes.data?.permissions || []);
      setFahrzeuge(fzRes.data || []);
      
      // Separate users into groups:
      // - leitung: WACHLEITUNG and WACHLEITUNG_VERTRETUNG roles (shown in separate section)
      // - developer: DEVELOPER role (hidden from everyone)
      // - regular: all others (shown in department columns)
      const grouped = {};
      abtRes.data.forEach(abt => {
        grouped[abt.id] = [];
      });
      
      // Special groups
      grouped['_leitung'] = [];
      
      usersRes.data.forEach(user => {
        // Skip DEVELOPER users completely - they should not appear anywhere
        if (user.rolle === 'DEVELOPER') {
          return;
        }
        // WACHLEITUNG and WACHLEITUNG_VERTRETUNG go to special Leitung section
        if (user.rolle === 'WACHLEITUNG' || user.rolle === 'WACHLEITUNG_VERTRETUNG') {
          grouped['_leitung'].push(user);
          return;
        }
        // Regular users go to their department
        if (user.abteilung_id && grouped[user.abteilung_id]) {
          grouped[user.abteilung_id].push(user);
        }
      });
      setUsersByAbteilung(grouped);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !canDragDrop) return;
    
    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const newUsersByAbteilung = { ...usersByAbteilung };
    const sourceUsers = [...newUsersByAbteilung[source.droppableId]];
    const destUsers = source.droppableId === destination.droppableId 
      ? sourceUsers 
      : [...newUsersByAbteilung[destination.droppableId]];

    const [movedUser] = sourceUsers.splice(source.index, 1);
    destUsers.splice(destination.index, 0, { ...movedUser, abteilung_id: destination.droppableId });

    newUsersByAbteilung[source.droppableId] = sourceUsers;
    newUsersByAbteilung[destination.droppableId] = destUsers;
    setUsersByAbteilung(newUsersByAbteilung);

    if (source.droppableId !== destination.droppableId) {
      try {
        await usersApi.update(draggableId, { abteilung_id: destination.droppableId });
        toast.success('Abteilung aktualisiert');
      } catch (error) {
        console.error('Update error:', error);
        toast.error('Aktualisierung fehlgeschlagen');
        loadData();
      }
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
  };

  const filteredUsersByAbteilung = {};
  Object.entries(usersByAbteilung).forEach(([abtId, users]) => {
    filteredUsersByAbteilung[abtId] = users
      .filter(u =>
        `${u.vorname} ${u.nachname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.loginname?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort(compareByDienstgrad);
  });

  // Count only regular users (not leitung)
  const totalUsers = Object.entries(usersByAbteilung)
    .filter(([key]) => key !== '_leitung')
    .reduce((sum, [, users]) => sum + users.length, 0);
  
  const leitungUsers = filteredUsersByAbteilung['_leitung'] || [];

  return (
    <MainLayout>
      <div className="min-h-full" data-testid="staff-page">
        {/* Header - sticky */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 pt-4 -mt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold">Mitarbeiter</h1>
              <p className="text-sm text-muted-foreground">
                {totalUsers} Mitarbeiter in {abteilungen.length} Abteilungen
                {leitungUsers.length > 0 && ` + ${leitungUsers.length} Leitung`}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48 sm:w-64 h-9"
                />
              </div>
              
              {canEdit && (
                <Button onClick={() => setAddDialogOpen(true)} data-testid="add-staff-btn" size="sm">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Hinzufügen</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Leitung Section - only visible if there are Wachleitung users */}
        {leitungUsers.length > 0 && (
          <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Users className="w-4 h-4 text-amber-700 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="font-bold text-amber-900 dark:text-amber-200">Wachleitung</h2>
                <p className="text-xs text-amber-700/70 dark:text-amber-400/70">Zentrale Führung</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {leitungUsers.map(user => (
                <div 
                  key={user.id}
                  onClick={() => handleUserClick(user)}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border shadow-sm hover:shadow-md cursor-pointer transition-all"
                >
                  {user.dienstgrad ? (
                    <DienstgradBadge dienstgrad={user.dienstgrad} size="sm" />
                  ) : (
                    <div className="w-8 h-10 rounded bg-muted flex items-center justify-center">
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-sm">{user.vorname} {user.nachname}</p>
                    <p className="text-xs text-muted-foreground">{roleLabels[user.rolle] || user.rolle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Board */}
        {isLoading ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="min-w-[260px] flex-1">
                <Skeleton className="h-64 rounded-xl" />
              </div>
            ))}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex gap-3 overflow-x-auto pb-4">
              {abteilungen.map(abt => (
                <DepartmentColumn
                  key={abt.id}
                  abteilung={abt}
                  users={filteredUsersByAbteilung[abt.id] || []}
                  onUserClick={handleUserClick}
                  canDragDrop={canDragDrop}
                  availableTags={availableTags}
                />
              ))}
            </div>
          </DragDropContext>
        )}

        {/* Dialogs */}
        <UserDetailDialog
          user={selectedUser}
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          abteilungen={abteilungen}
          onUpdate={loadData}
          canEdit={canEdit && canManageRole(currentUser?.rolle, selectedUser?.rolle)}
          canDelete={canDelete && canManageRole(currentUser?.rolle, selectedUser?.rolle)}
          availableTags={availableTags}
          availableKategorien={availableKategorien}
          canSetZusatzberechtigungen={canSetZusatzberechtigungen && canManageRole(currentUser?.rolle, selectedUser?.rolle)}
          availablePermissions={availablePermissions}
          currentUserRolle={currentUser?.rolle}
          fahrzeuge={fahrzeuge}
        />
        
        <AddUserDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          abteilungen={abteilungen}
          onSuccess={loadData}
          currentUserRolle={currentUser?.rolle}
          currentUserAbteilungId={currentUser?.abteilung_id}
          availableKategorien={availableKategorien}
          availableTags={availableTags}
        />
      </div>
    </MainLayout>
  );
}

export default MitarbeiterPage;
